import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, BookOpen, Edit3, Save, Loader2, X, Play } from "lucide-react";
import CloudinaryUpload from "../components/CloudinaryUpload";

export default function Biography() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [bio, setBio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/biography").then(r => { setBio(r.data); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const wordCount = (bio?.body || "").trim().split(/\s+/).filter(Boolean).length;

  const save = async (publish = false) => {
    setErr(""); setMsg(""); setSaving(true);
    try {
      const { data } = await api.put("/admin/biography", {
        title: bio.title, subtitle: bio.subtitle, body: bio.body,
        photos: bio.photos || [], videos: bio.videos || [],
        published: publish,
      });
      setBio(data);
      setEditing(false);
      setMsg(publish ? "Biography published ✓" : "Draft saved ✓");
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  const addPhoto = (url) => setBio(b => ({ ...b, photos: [...(b.photos || []), url].slice(0, 10) }));
  const rmPhoto = (i) => setBio(b => ({ ...b, photos: (b.photos || []).filter((_, k) => k !== i) }));
  const addVideo = (url) => setBio(b => ({ ...b, videos: [...(b.videos || []), url].slice(0, 2) }));
  const rmVideo = (i) => setBio(b => ({ ...b, videos: (b.videos || []).filter((_, k) => k !== i) }));

  if (loading) return <div className="px-4 py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#FF7A00] mx-auto"/></div>;
  if (!bio) return null;

  return (
    <div className="px-4 py-5 pb-24 max-w-3xl mx-auto" data-testid="biography-page">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => nav(-1)} data-testid="bio-back" className="p-2 rounded-full bg-white/8"><ArrowLeft className="w-4 h-4"/></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-outfit font-extrabold text-2xl leading-tight flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#FF7A00]"/>Biography
          </h1>
          <p className="text-white/60 text-xs">The Hifireels story</p>
        </div>
        {isAdmin && !editing && (
          <button onClick={() => setEditing(true)} data-testid="bio-edit-btn"
            className="px-3 py-2 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] font-bold text-xs flex items-center gap-1 active:scale-95">
            <Edit3 className="w-3.5 h-3.5"/>Edit
          </button>
        )}
      </div>

      {msg && <div className="mb-3 p-2 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-xs">{msg}</div>}

      {!editing ? (
        !bio.published && !isAdmin ? (
          <div className="text-center py-16 text-white/55">
            <BookOpen className="w-14 h-14 mx-auto mb-2 opacity-40 text-[#FF7A00]"/>
            <p className="font-outfit font-bold text-lg">Biography coming soon</p>
            <p className="text-xs mt-1">Our team is preparing this page for you.</p>
          </div>
        ) : (
          <>
            <h2 className="font-outfit font-extrabold text-3xl mb-1" data-testid="bio-title">{bio.title}</h2>
            {bio.subtitle && <p className="text-white/60 text-sm mb-4" data-testid="bio-subtitle">{bio.subtitle}</p>}
            {!bio.published && isAdmin && (
              <div className="mb-4 p-2 rounded-2xl bg-[#FF7A00]/10 border border-[#FF7A00]/30 text-[#FF7A00] text-xs">Draft — only you (admin) can see this</div>
            )}
            {(bio.photos || []).length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-5" data-testid="bio-photos">
                {bio.photos.map((p, i) => (
                  <img key={i} src={p} alt="" className="aspect-square object-cover rounded-2xl border border-white/10"/>
                ))}
              </div>
            )}
            {(bio.videos || []).length > 0 && (
              <div className="grid grid-cols-2 gap-2 mb-5" data-testid="bio-videos">
                {bio.videos.map((v, i) => (
                  <video key={i} src={v} controls playsInline className="w-full aspect-video rounded-2xl border border-white/10"/>
                ))}
              </div>
            )}
            <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap text-white/85 leading-relaxed" data-testid="bio-body">{bio.body || <em className="text-white/40">No content yet.</em>}</div>
            <div className="mt-6 text-[10px] text-white/40">{wordCount.toLocaleString()} words · Last updated {bio.updated_at ? new Date(bio.updated_at).toLocaleString() : "never"}</div>
          </>
        )
      ) : (
        <div className="space-y-3" data-testid="bio-editor">
          <input value={bio.title || ""} onChange={e => setBio(b => ({ ...b, title: e.target.value }))} maxLength={200}
            placeholder="Title" data-testid="bio-title-input"
            className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/15 outline-none focus:border-[#FF7A00] font-outfit font-bold text-xl"/>
          <input value={bio.subtitle || ""} onChange={e => setBio(b => ({ ...b, subtitle: e.target.value }))} maxLength={300}
            placeholder="Subtitle (optional)" data-testid="bio-subtitle-input"
            className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00]"/>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1 block">Photo Gallery ({(bio.photos || []).length}/10)</label>
            {(bio.photos || []).length > 0 && (
              <div className="grid grid-cols-4 gap-2 mb-2">
                {bio.photos.map((p, i) => (
                  <div key={i} className="relative aspect-square" data-testid={`bio-photo-${i}`}>
                    <img src={p} alt="" className="w-full h-full object-cover rounded-xl border border-white/10"/>
                    <button onClick={() => rmPhoto(i)} className="absolute -top-1 -right-1 p-1 rounded-full bg-[#FF2A5F]" data-testid={`bio-photo-rm-${i}`}><X className="w-3 h-3"/></button>
                  </div>
                ))}
              </div>
            )}
            {(bio.photos || []).length < 10 && (
              <CloudinaryUpload kind="image" maxMB={5} testId="bio-photo-upload"
                onUploaded={(d) => addPhoto(d.secure_url)}/>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1 block">Videos ({(bio.videos || []).length}/2)</label>
            {(bio.videos || []).length > 0 && (
              <div className="space-y-2 mb-2">
                {bio.videos.map((v, i) => (
                  <div key={i} className="relative" data-testid={`bio-video-${i}`}>
                    <video src={v} controls className="w-full max-h-48 rounded-xl border border-white/10"/>
                    <button onClick={() => rmVideo(i)} className="absolute top-1 right-1 p-1 rounded-full bg-[#FF2A5F]" data-testid={`bio-video-rm-${i}`}><X className="w-3 h-3"/></button>
                  </div>
                ))}
              </div>
            )}
            {(bio.videos || []).length < 2 && (
              <CloudinaryUpload kind="video" maxMB={100} testId="bio-video-upload"
                onUploaded={(d) => addVideo(d.secure_url)}/>
            )}
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-white/60 font-bold mb-1 flex items-center justify-between">
              <span>Biography Text</span>
              <span className={`text-[10px] font-normal normal-case ${wordCount >= 1500 ? "text-emerald-400" : "text-[#FF2A5F]"}`}>
                {wordCount.toLocaleString()} / 1,500 words minimum to publish
              </span>
            </label>
            <textarea rows={16} value={bio.body || ""} onChange={e => setBio(b => ({ ...b, body: e.target.value }))}
              placeholder="Write the biography here (min 1,500 words to publish)…" data-testid="bio-body-input"
              className="w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF7A00] leading-relaxed"/>
          </div>

          {err && <div className="text-[#FF2A5F] text-xs" data-testid="bio-err">{err}</div>}

          <div className="flex gap-2 sticky bottom-4 bg-[#0B0F19]/95 backdrop-blur -mx-4 px-4 py-3 border-t border-white/10">
            <button onClick={() => { setEditing(false); load(); }} className="px-4 py-3 rounded-full bg-white/8 border border-white/15 font-bold" data-testid="bio-cancel">Cancel</button>
            <button onClick={() => save(false)} disabled={saving} data-testid="bio-save-draft"
              className="flex-1 py-3 rounded-full bg-white/10 border border-white/15 font-outfit font-bold flex items-center justify-center gap-1 disabled:opacity-50 active:scale-95">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Save Draft
            </button>
            <button onClick={() => save(true)} disabled={saving || wordCount < 1500} data-testid="bio-publish"
              className="flex-1 py-3 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FF2A5F] text-black font-outfit font-black flex items-center justify-center gap-1 disabled:opacity-40 active:scale-95">
              Publish
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
